import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { BASE_URL } from "../utils/reportsHelpers";
import text from "../constants/reportsTranslations";

const useReportsData = (language) => {
  const { profile } = useProfile();
  const [reportsData, setReportsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Tracks the initial mount-time fetch in fetchReports() below (distinct
  // from `isLoading`, which only covers the "generate new report" action).
  // Starts true so the page-level skeleton is guaranteed to be visible on
  // first paint instead of racing the mount effect that flips it.
  const [reportsLoading, setReportsLoading] = useState(true);
  const [emailStatus, setEmailStatus] = useState({ type: "", message: "" });
  const [quickStats, setQuickStats] = useState({
    totalReports: 24,
    generatedThisMonth: 8,
    totalDownloads: 1247,
    avgGenerationTime: "3.2 min",
    activeUsers: 45,
    diskUsage: "78%",
    successRate: "98.5%",
    avgRating: 4.6
  });

  // Derive infinityCode from profile (with localStorage fallback)
  const currentUser = profile?.infinityCode
    ? String(profile.infinityCode)
    : localStorage.getItem('infinityCode') || null;

  // permissions: derive from stored adminLevel and role
  const [reportFeatures, setReportFeatures] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [viewType, setViewType] = useState("");

  useEffect(() => {
    const storedAdminLevel = localStorage.getItem("adminLevel");
    const storedRoleRaw = profile?.role || localStorage.getItem("role");
    const storedViewType = localStorage.getItem("viewType");

    if (storedRoleRaw) setUserRole(String(storedRoleRaw).toLowerCase());
    if (storedViewType) setViewType(String(storedViewType).toLowerCase());

    if (storedAdminLevel) {
      try {
        const parsed = JSON.parse(storedAdminLevel);
        setReportFeatures(parsed?.features?.reports || []);
      } catch (e) {
        // ignore parse error
      }
    }
  }, [profile]);

  const isPrivilegedRole = userRole === "admin" || userRole === "superadmin";
  const shouldEnforceReportPermissions = viewType === "admin";

  const canDownloadReports =
    !shouldEnforceReportPermissions || isPrivilegedRole || reportFeatures.includes("download");

  // "view" was never a real registry feature for this module (only
  // "download" exists) — this flag is currently unused, but fixed to avoid
  // a landmine if it's ever wired up to a control.
  const canViewReports =
    !shouldEnforceReportPermissions || isPrivilegedRole || reportFeatures.includes("download");

  useEffect(() => {
    const fetchReports = async () => {
      if (!currentUser) {
        console.log('⏳ Waiting for user info...');
        return;
      }

      try {
        setReportsLoading(true);
        console.log('📊 Fetching reports for infinityCode:', currentUser);

        const res = await apiFetch(
          `${BASE_URL}/reports/metadata?infinityCode=${currentUser}`,
          { method: 'GET' }
        );


        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        console.log('✅ Reports loaded:', data.reports?.length);

        setReportsData(data.reports || []);

        if (data.statistics) {
          setQuickStats({
            totalReports: data.reports?.length || 0,
            generatedThisMonth: data.statistics.attendanceThisMonth || 0,
            totalDownloads: 1247,
            avgGenerationTime: "3.2 " + text[language].min,
            totalEmployees: data.statistics.totalEmployees || 0,
            activeEmployees: data.statistics.activeEmployees || 0,
            avgAttendanceRate: data.statistics.attendanceRate || '0%',
            recentHires: data.statistics.recentHires || 0
          });
        }
      } catch (err) {
        console.error("❌ Error fetching reports:", err);
        setEmailStatus({
          type: "error",
          message: err.message || text[language].errorGeneratingReport
        });
      } finally {
        setReportsLoading(false);
      }
    };

    fetchReports();
  }, [currentUser, language]);

  const generateNewReport = async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filters: {
            requestingUser: currentUser
          }
        })
      });

      const newReport = await res.json();

      setReportsData(prev => [...prev, newReport]);
      setEmailStatus({
        type: "success",
        message: text[language].reportGenerateSuccess
      });
    } catch (err) {
      setEmailStatus({
        type: "error",
        message: text[language].errorGeneratingReport
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadReport = async (report, selectedFormat) => {
    if (!currentUser) {
      setEmailStatus({
        type: "error",
        message: text[language].userInfoNotAvailable
      });
      return;
    }

    if (!canDownloadReports) {
      setEmailStatus({ type: "error", message: text[language].errorDownloadingReport });
      return;
    }

    try {
      const format = selectedFormat || 'pdf';

      const response = await apiFetch(`${BASE_URL}/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: report.category,
          format: format,
          filters: {
            requestingUser: currentUser
          }
        })
      });

      if (!response.ok) {
        throw new Error(text[language].errorDownloadingReport);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const extension = format === 'xlsx' ? 'xlsx' : 'pdf';
      a.download = `${report.category}-report-${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setEmailStatus({
        type: "success",
        message: text[language].reportDownloadSuccess
      });
    } catch (error) {
      setEmailStatus({
        type: "error",
        message: text[language].errorDownloadingReport
      });
    }
  };
const toggleBookmark = (reportId) => {
  setReportsData(prev =>
    prev.map(report =>
      report.id === reportId ? { ...report, isBookmarked: !report.isBookmarked } : report
    )
  );
};

return {
  reportsData,
  setReportsData,
  currentUser,
  isLoading,
  reportsLoading,
  quickStats,
  emailStatus,
  setEmailStatus,
  generateNewReport,
  handleDownloadReport,
  canDownloadReports,
  canViewReports,
  toggleBookmark,
};
};

export default useReportsData;